import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'

import { httpOk, GET, POST, DELETE } from 'helpers/api'

// Ответ ассистента приходит текстом, а внутри него пути кабинета — «загляните
// в /analytics/rfm». Ссылку делаем кликабельной, но только если такая страница
// действительно есть в меню пользователя: иначе совет уводит в пустоту.
function renderAnswer(text, allowedPaths) {
	if (!text) return null

	const parts = String(text).split(/(\/[a-z0-9-]+(?:\/[a-z0-9-]+)*)/gi)

	return parts.map((part, index) => {
		const target = resolvePath(part, allowedPaths)
		if (target) {
			return <Link to={target} key={index} className="fw-bold text-decoration-underline">{part}</Link>
		}
		return <React.Fragment key={index}>{part}</React.Fragment>
	})
}

/**
 * Путь из ответа → реальный адрес страницы.
 *
 * Модель иногда сокращает адрес («/loyalty» вместо «/loyalty/services»), поэтому
 * кроме точного совпадения принимаем единственное продолжение: если под префикс
 * подходит ровно одна страница меню, ссылка ведёт на неё. Несколько кандидатов —
 * оставляем текстом, гадать нельзя.
 */
function resolvePath(part, allowedPaths) {
	if (!part || !part.startsWith('/')) return null
	if (allowedPaths.has(part)) return part

	const candidates = [...allowedPaths].filter(path => path.startsWith(part + '/'))

	return candidates.length === 1 ? candidates[0] : null
}

// Пока ответ считается, важно показывать, что работа идёт: одинаковая надпись
// дольше десяти секунд читается как зависшая страница.
function LoadingBubble({ seconds, t }) {
	const stage = seconds < 4
		? t('ai_thinking')
		: (seconds < 12 ? t('ai_thinking_reports') : t('ai_thinking_answer'))

	return (
		<div className="d-flex mb-3">
			<div className="p-3 rounded bg-light text-muted d-flex align-items-center gap-2">
				<span className="spinner-border spinner-border-sm" role="status"></span>
				<span>{stage}</span>
				<span style={{ fontSize: '11px', opacity: 0.7 }}>{seconds} с</span>
			</div>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [poses, setPoses] = useState([])
	const [suggestions, setSuggestions] = useState([])
	const [scope, setScope] = useState('pos')
	const [posId, setPosId] = useState(reduxAccount.pos_id)
	const [messages, setMessages] = useState([])
	const [chatId, setChatId] = useState(null)
	const [chats, setChats] = useState([])
	const [question, setQuestion] = useState('')
	const [images, setImages] = useState([])
	const [loading, setLoading] = useState(false)
	const [loadingSeconds, setLoadingSeconds] = useState(0)
	const [limitLeft, setLimitLeft] = useState(null)
	const [showHistory, setShowHistory] = useState(false)
	const [allowedPaths, setAllowedPaths] = useState(new Set())

	const messagesEndRef = useRef(null)

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getLimits() {
		const response = await GET('/ai-assistant/limits')
		if (httpOk(response)) setLimitLeft(response.data.limit_left)
	}

	// Пути из меню пользователя — по ним решаем, какую ссылку в ответе делать
	// кликабельной. Меню приходит уже с учётом прав, поэтому лишнего не покажем.
	async function getAllowedPaths() {
		const response = await GET('/users/navigation')
		if (!httpOk(response)) return

		const paths = new Set()
		const walk = (items) => {
			(items ?? []).forEach(item => {
				if (item.url && item.url.startsWith('/')) paths.add(item.url)
				walk(item.children)
			})
		}
		walk(response.data.data)
		setAllowedPaths(paths)
	}

	// Быстрые вопросы приходят с сервера: он же решает, какие показывать — кнопку,
	// на которую у пользователя нет права, лучше не рисовать вовсе.
	async function getSuggestions() {
		const response = await GET('/ai-assistant/suggestions')
		if (httpOk(response)) setSuggestions(response.data.groups)
	}

	async function getChats() {
		const response = await GET('/ai-assistant/chats', { size: 20 })
		if (httpOk(response)) setChats(response.data.data)
	}

	async function openChat(id) {
		const response = await GET(`/ai-assistant/chats/${id}`)
		if (httpOk(response)) {
			setChatId(id)
			setMessages(response.data.messages.map(item => ({
				id: item.id,
				role: item.role,
				content: item.content,
				rating: item.rating,
				tools: item.tools_used ? JSON.parse(item.tools_used).map(tool => tool.name) : [],
			})))
			setShowHistory(false)
		}
	}

	async function deleteChat(id) {
		const response = await DELETE(`/ai-assistant/chats/${id}`)
		if (httpOk(response)) {
			getChats()
			if (id === chatId) newChat()
		}
	}

	function newChat() {
		setChatId(null)
		setMessages([])
		setQuestion('')
		setImages([])
	}

	async function ask(text) {
		const value = (text ?? question).trim()
		if (!value || loading) return

		const outgoing = { role: 'user', content: value, images: images.length }
		setMessages(prev => [...prev, outgoing])
		setQuestion('')
		setLoading(true)

		// notification: false — success-тост на каждый ответ в чате только мешает.
		const response = await POST('/ai-assistant', {
			question: value,
			scope: scope,
			pos_id: scope === 'brand' ? null : posId,
			chat_id: chatId,
			lang: localStorage.getItem('lang') ?? 'ru',
			images: images,
			screen: { route: window.location.pathname },
		}, { notification: false })

		setImages([])
		setLoading(false)

		if (httpOk(response)) {
			setChatId(response.data.chat_id)
			setLimitLeft(response.data.limit_left)
			setMessages(prev => [...prev, {
				id: response.data.message_id,
				role: 'assistant',
				content: response.data.answer,
				tools: response.data.tools_used ?? [],
				scope: response.data.scope,
			}])
			getChats()
		} else {
			// Ответ не пришёл — вопрос из ленты не убираем, но помечаем неудачу,
			// иначе выглядит как будто сообщение просто пропало.
			setMessages(prev => [...prev, {
				role: 'assistant',
				content: response?.data?.message ?? t('error'),
				failed: true,
			}])
		}
	}

	async function rate(index, rating) {
		const message = messages[index]
		if (!message?.id) return

		const response = await POST('/ai-assistant/feedback', {
			message_id: message.id,
			rating: rating,
		}, { notification: false })

		if (httpOk(response)) {
			setMessages(prev => prev.map((item, key) => key === index ? { ...item, rating: rating } : item))
		}
	}

	// Скриншот из буфера: сжимаем на клиенте, чтобы не гонять мегабайты и не платить
	// за лишние токены — для «что это за кнопка» хватает 1600px по длинной стороне.
	function handlePaste(e) {
		const files = Array.from(e.clipboardData?.files ?? [])
		if (files.length === 0) return

		e.preventDefault()
		files.slice(0, 2).forEach(file => {
			if (!file.type.startsWith('image/')) return
			compressImage(file, (data) => {
				setImages(prev => [...prev, { mime: 'image/jpeg', data: data }].slice(0, 2))
			})
		})
	}

	function compressImage(file, callback) {
		const reader = new FileReader()
		reader.onload = (event) => {
			const image = new Image()
			image.onload = () => {
				const max = 1600
				let width = image.width
				let height = image.height
				if (width > max || height > max) {
					const ratio = Math.min(max / width, max / height)
					width = Math.round(width * ratio)
					height = Math.round(height * ratio)
				}

				const canvas = document.createElement('canvas')
				canvas.width = width
				canvas.height = height
				canvas.getContext('2d').drawImage(image, 0, 0, width, height)
				callback(canvas.toDataURL('image/jpeg', 0.8).split(',')[1])
			}
			image.src = event.target.result
		}
		reader.readAsDataURL(file)
	}

	function handleKeyDown(e) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			ask()
		}
	}

	function changeScope(value) {
		if (value === 'brand') {
			setScope('brand')
		} else {
			setScope('pos')
			setPosId(Number(value))
		}
		// Область — часть контекста: продолжать старый диалог с новыми точками нельзя,
		// иначе «а за прошлый месяц?» посчитается не по той точке.
		newChat()
	}

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages, loading])

	// Секундомер ожидания: он же двигает подпись под спиннером
	useEffect(() => {
		if (!loading) {
			setLoadingSeconds(0)
			return
		}

		const timer = setInterval(() => setLoadingSeconds(prev => prev + 1), 1000)
		return () => clearInterval(timer)
	}, [loading])

	useEffect(() => {
		getPoses()
		getLimits()
		getChats()
		getSuggestions()
		getAllowedPaths()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex align-items-center gap-2">
					<h4 className="vertical-center mb-0">{t('ai_assistant')}</h4>
					<span className="badge bg-warning">{t('test_mode')}</span>
				</div>

				<div className="d-flex align-items-center gap-2">
					{limitLeft !== null &&
						<span className="text-muted" style={{ fontSize: '12px' }}>
							{t('ai_questions_left')}: {limitLeft}
						</span>
					}

					<select className="form-select" style={{ width: 'auto' }}
						value={scope === 'brand' ? 'brand' : posId}
						onChange={(e) => changeScope(e.target.value)}>
						{poses.map((item, index) => (
							<option value={item.id} key={index}>{item.name}</option>
						))}
						{poses.length > 1 &&
							<option value="brand">{t('all_poses')}</option>
						}
					</select>

					<button className="btn btn-outline-primary" onClick={() => setShowHistory(!showHistory)}>
						<i className="uil-history"></i>
					</button>

					<button className="btn btn-primary" onClick={() => newChat()}>
						{t('ai_new_chat')}
					</button>
				</div>
			</div>

			{/* Чат занимает всю свободную высоту экрана: 120px — это шапка кабинета (60),
				заголовок страницы (40) и отступы (20). Иначе поле ввода висит посреди
				экрана, а под ним остаётся пустое место */}
			<div className="row" style={{ height: 'calc(100vh - 120px)' }}>
				<div className={`${showHistory ? 'col-md-9' : 'col-md-12'} h-100`}>
					<div className="card h-100 mb-0">
						<div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>

							{/* ЛЕНТА СООБЩЕНИЙ */}
							{/* overflowX: hidden — из-за вертикальной прокрутки браузер включает и
								горизонтальную, а отрицательные отступы .row внутри дают лишние 24px
								и полосу под карточками подсказок.
								minHeight: 0 обязателен: без него flex-элемент не сжимается ниже
								содержимого и прокрутка уезжает за край экрана вместе с полем ввода */}
							<div className="flex-grow-1 mb-3" style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
								{messages.length === 0 && !loading &&
									<div>
										<div className="alert alert-warning" role="alert">
											<i className="uil-exclamation-triangle me-1"></i>
											{t('ai_disclaimer')}
										</div>

										<div className="row">
											{suggestions.map((group, index) => (
												<div className="col-md-6 mb-3" key={index}>
													<div className="border rounded p-3 h-100">
														<div className="fw-bold">
															<i className={`uil ${group.icon} me-1`}></i>
															{t(group.title_key)}
														</div>
														<div className="text-muted mb-2" style={{ fontSize: '12px' }}>
															{t(group.hint_key)}
														</div>
														{group.items.map((item, key) => (
															<button type="button" className="btn btn-outline-secondary btn-sm me-2 mb-2"
																key={key}
																onClick={() => ask(t(item.key))}>
																{t(item.key)}
															</button>
														))}
													</div>
												</div>
											))}
										</div>
									</div>
								}

								{messages.map((item, index) => (
									<div className={`d-flex mb-3 ${item.role === 'user' ? 'justify-content-end' : ''}`} key={index}>
										<div className={`p-3 rounded ${item.role === 'user' ? 'bg-primary text-white' : 'bg-light'}`}
											style={{ maxWidth: '80%', whiteSpace: 'pre-wrap' }}>
											{item.role === 'assistant'
												? renderAnswer(item.content, allowedPaths)
												: item.content}

											{item.role === 'assistant' && !item.failed &&
												<div className="d-flex align-items-center gap-2 mt-2">
													{!!item.scope &&
														<span className="text-muted" style={{ fontSize: '11px' }}>
															{item.scope.pos_names?.join(', ')}
														</span>
													}
													{!!item.id &&
														<>
															<button type="button"
																className={`btn btn-sm ${item.rating === 1 ? 'btn-success' : 'btn-outline-secondary'}`}
																onClick={() => rate(index, 1)}>
																<i className="uil-thumbs-up"></i>
															</button>
															<button type="button"
																className={`btn btn-sm ${item.rating === -1 ? 'btn-danger' : 'btn-outline-secondary'}`}
																onClick={() => rate(index, -1)}>
																<i className="uil-thumbs-down"></i>
															</button>
														</>
													}
												</div>
											}
										</div>
									</div>
								))}

								{loading && <LoadingBubble seconds={loadingSeconds} t={t} />}

								<div ref={messagesEndRef}></div>
							</div>

							{/* ПОЛЕ ВВОДА */}
							{images.length > 0 &&
								<div className="d-flex gap-2 mb-2">
									{images.map((item, index) => (
										<div className="position-relative" key={index}>
											<img src={`data:${item.mime};base64,${item.data}`} alt=""
												style={{ height: '60px', borderRadius: '6px' }} />
											<button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 px-1"
												onClick={() => setImages(prev => prev.filter((_, key) => key !== index))}>
												<i className="uil-times"></i>
											</button>
										</div>
									))}
								</div>
							}

							<div className="d-flex gap-2">
								<textarea className="form-control" rows="2"
									placeholder={t('ai_placeholder')}
									value={question}
									disabled={loading}
									onPaste={handlePaste}
									onKeyDown={handleKeyDown}
									onChange={(e) => setQuestion(e.target.value)}>
								</textarea>

								<button className="btn btn-primary" disabled={loading || !question.trim()}
									onClick={() => ask()}>
									<i className="uil-message"></i>
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* ИСТОРИЯ ДИАЛОГОВ */}
				{showHistory &&
					<div className="col-md-3 h-100">
						<div className="card h-100 mb-0">
							<div className="card-body" style={{ overflowY: 'auto' }}>
								<div className="fw-bold mb-2">{t('history')}</div>

								{chats.length === 0 &&
									<div className="text-muted" style={{ fontSize: '12px' }}>{t('no_data')}</div>
								}

								{chats.map((item, index) => (
									<div className="d-flex justify-content-between align-items-center mb-2" key={index}>
										<button type="button"
											className={`btn btn-sm text-start flex-grow-1 ${item.id === chatId ? 'btn-primary' : 'btn-outline-secondary'}`}
											onClick={() => openChat(item.id)}>
											<span style={{ fontSize: '12px' }}>{item.title}</span>
										</button>
										<button type="button" className="btn btn-sm btn-outline-danger ms-1"
											onClick={() => deleteChat(item.id)}>
											<i className="uil-trash-alt"></i>
										</button>
									</div>
								))}
							</div>
						</div>
					</div>
				}
			</div>
		</>
	)
}

export default Index
